// Phase 6 seam for the double-entry ledger (lead-owned). P6-A implements FINANCE-01; P6-B (refunds,
// returns, COD), P6-D (POS drawer and shifts) and P6-A itself (payments, settlements) post through it.
// Specs: DATA_MODEL finance rows (journal_entries / journal_lines), FEATURE_MATRIX FINANCE-001…004.
import type { Paisa } from "./commerce";

/**
 * The fixed chart every store gets (seeded per store on first use). Custom accounts may be added by
 * the merchant later; system postings only use these codes.
 */
export const LEDGER_ACCOUNT_CODES = [
  "cash", // cash on hand outside a POS drawer
  "drawer", // POS cash drawers (per register sub-ledger via `dimension`)
  "bank",
  "clearing", // online gateway money not yet settled to the bank
  "courier_receivable", // COD collected by a courier, not yet remitted
  "sales",
  "shipping_income",
  "discounts",
  "vat_payable",
  "refunds",
  "fees", // gateway and courier fees
  "cod_shortfall",
  "cod_suspense", // COD money a remittance paid that matches no open receivable (unknown, duplicate, over)
  "store_credit_liability",
  "over_short", // POS shift over/short
] as const;
export type LedgerAccountCode = (typeof LEDGER_ACCOUNT_CODES)[number];

export interface LedgerLine {
  account: LedgerAccountCode;
  /** Exactly one of debit/credit is > 0 per line. */
  debit_paisa: Paisa;
  credit_paisa: Paisa;
  /** Optional sub-ledger key, e.g. a register ID for `drawer`, a courier account for receivables. */
  dimension?: string;
}

export interface LedgerPosting {
  org_id: string;
  store_id: string;
  /** Idempotency: one entry per (source_type, source_id, reversal_of). A replay returns the same entry. */
  source_type: string; // e.g. "payment", "refund", "cod_remittance", "settlement", "shift_close"
  source_id: string;
  reversal_of?: string;
  occurred_at: string;
  memo: string;
  lines: LedgerLine[];
}

/** Σ debit = Σ credit is enforced by the poster (an unbalanced posting is refused, never stored). */
export interface LedgerPoster {
  post(posting: LedgerPosting): Promise<{ entry_id: string; replayed: boolean }>;
}
